import * as React from "react";
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";

import { TemplateField } from "@/shared/components/TemplateField";
import { type AgentSubject, goalTypeLabel } from "@/shared/domain/agents";
import { EntityIcon, MessageCircle, Plus } from "@/shared/foundation/icons";
import {
  Banner,
  ConfirmDialog,
  Field,
  FormBlock,
  FormSection,
  Radio,
  RadioGroup,
  RobotoButton,
} from "@/shared/foundation/primitives";
import { agentFormSectionId } from "@/shared/navigation";
import { pluralize } from "@/shared/text";

import {
  anyGoalUsesImplicitCollection,
  type EditorSubject,
  goalForSubject,
} from "../../Agents/buildRequestTemplate";
import {
  MAX_GOALS,
  newGoalValues,
  type AgentFormValues,
} from "../agentFormSchema";
import { GoalCard } from "../goals/GoalCard";
import { DEFAULT_GOAL_KINDS } from "../goals/goalKinds";
import cards from "../goals/goals.module.css";
import { useAgentVariableCatalog } from "../useAgentVariableCatalog";
import { VariablesBlock } from "../VariablesBlock";

import styles from "./sections.module.css";

/**
 * What the agent is for: what it runs on, then the goals it must finish on
 * that record, whatever else it should be told before its first turn, and the
 * variables both of those declare.
 *
 * What it runs on comes first and decides the rest, so a new agent shows
 * nothing else until it is picked. An agent on nothing specific has no goals:
 * a goal works on one dataset or session.
 *
 * Switching what an agent with goals runs on asks first: to the other kind of
 * record, each goal becomes the same kind of goal there; to nothing specific,
 * the goals are removed.
 *
 * Goals are independent of one another, so the list keeps insertion order and
 * offers no reordering.
 */
export function RunsOnSection() {
  const { control, getFieldState, formState, getValues, setValue } =
    useFormContext<AgentFormValues>();
  const { replace } = useFieldArray({ control, name: "goals" });
  const subject = useWatch({ control, name: "subject" });
  const passthroughGoals = useWatch({ control, name: "passthroughGoals" });
  const subjectError = getFieldState("subject", formState).error;
  // The subject a person picked while the agent has goals, held until they
  // confirm the dialog that says what the switch does to those goals.
  const [pendingSubject, setPendingSubject] =
    React.useState<EditorSubject | null>(null);

  const switchSubject = (next: EditorSubject) => {
    setValue("subject", next, { shouldDirty: true });
    if (next === "none") {
      replace([]);
      setValue("passthroughGoals", [], { shouldDirty: true });
    } else {
      replace(getValues("goals").map((goal) => goalForSubject(goal, next)));
    }
  };

  return (
    <FormSection
      title="Runs on"
      titleId={agentFormSectionId("goals")}
      hint="What the agent works on each time it's launched."
    >
      <div className={styles.groups} data-testid="agent-subject">
        <RadioGroup<EditorSubject>
          appearance="card"
          size="md"
          aria-labelledby={agentFormSectionId("goals")}
          value={subject}
          error={subjectError?.message}
          onValueChange={(next) => {
            // Goals set through the SDK or API stay as they are on a switch
            // between a dataset and a session; only a switch to nothing
            // specific removes them, so only that switch asks about them.
            const affectedGoalCount =
              getValues("goals").length +
              (next === "none" ? passthroughGoals.length : 0);
            if (affectedGoalCount === 0) {
              switchSubject(next);
            } else {
              setPendingSubject(next);
            }
          }}
        >
          <Radio
            value="dataset"
            label="A dataset"
            description="Launched from a dataset page, or by a trigger on a dataset event."
            icon={<EntityIcon type="dataset" />}
          />
          <Radio
            value="session"
            label="A session"
            description="Launched from a session page, or by a trigger on session.ingested."
            icon={<EntityIcon type="session" />}
          />
          <Radio
            value="none"
            label="Nothing specific"
            description="Kickoff message only. Launched from the agent's page or a trigger."
            icon={<MessageCircle aria-hidden size={16} />}
          />
        </RadioGroup>

        {pendingSubject !== null && (
          <SwitchSubjectDialog
            next={pendingSubject}
            goals={getValues("goals")}
            keptGoalCount={passthroughGoals.length}
            onCancel={() => {
              setPendingSubject(null);
            }}
            onConfirm={() => {
              switchSubject(pendingSubject);
              setPendingSubject(null);
            }}
          />
        )}

        {subject === null && (
          <p className={styles.unpicked} data-testid="agent-subject-unpicked">
            Pick what the agent runs on to set its goals and kickoff message.
          </p>
        )}
        {subject === "none" && (
          <Banner
            variant="info"
            title="No goals."
            description="Goals work on one dataset or session, so this agent runs on its kickoff message alone. Launch it from its own page or from a trigger; it won't appear on dataset or session pages."
            data-testid="agent-no-goals"
          />
        )}
        {(subject === "dataset" || subject === "session") && (
          <GoalsBlock subject={subject} />
        )}
        {subject !== null && (
          <>
            <KickoffBlock subject={subject} />
            <VariablesBlock />
          </>
        )}
      </div>
    </FormSection>
  );
}

/**
 * The goals an agent on `subject` must finish before its turn is done: none
 * is a valid answer, with the kickoff message then saying what to do.
 */
function GoalsBlock({ subject }: { subject: AgentSubject }) {
  const { control, getFieldState, formState } =
    useFormContext<AgentFormValues>();
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "goals",
  });
  const titleId = React.useId();
  const listError = getFieldState("goals", formState).error;
  const passthroughGoals = useWatch({ control, name: "passthroughGoals" });
  const keptGoalNames = passthroughGoals.map(({ goal }) =>
    goalTypeLabel(String(goal.goal_type)),
  );
  const goalCount = fields.length + keptGoalNames.length;

  return (
    <FormBlock
      titleId={titleId}
      title={
        <>
          Goals <span className={styles.qualifier}>(optional)</span>
        </>
      }
      hint={`Work the agent has to finish on the ${subject} before its turn is done.`}
      actions={
        <span className={styles.count} data-testid="agent-goals-count">
          {goalCount} {pluralize("goal", goalCount)}
        </span>
      }
      className={styles.divided}
    >
      {keptGoalNames.length > 0 && (
        <Banner
          variant="info"
          title={`Set through the SDK or API: ${keptGoalNames.join(", ")}`}
          description="The editor cannot change a goal set this way. Saving the agent keeps it as it is."
          data-testid="agent-kept-goals"
        />
      )}
      <div className={cards.goals}>
        {goalCount === 0 && (
          <p className={styles.noGoals} data-testid="agent-goals-empty">
            No goals. The agent works on the {subject} from its kickoff message
            alone, and still appears under Launch agent on every {subject} page.
          </p>
        )}
        {fields.length > 0 && (
          <div
            className={cards.list}
            data-testid="agent-goals"
            data-count={fields.length}
          >
            {fields.map((field, index) => (
              <GoalCard
                key={field.id}
                index={index}
                onRemove={() => {
                  remove(index);
                }}
                onChangeKind={(kind) => {
                  update(index, newGoalValues(kind));
                }}
              />
            ))}
          </div>
        )}

        <div>
          <RobotoButton
            variant="secondary"
            icon={<Plus aria-hidden size={16} />}
            disabled={goalCount >= MAX_GOALS}
            onClick={() => {
              append(newGoalValues(DEFAULT_GOAL_KINDS[subject]));
            }}
            data-testid="agent-add-goal"
          >
            Add goal
          </RobotoButton>
        </div>

        {listError?.message !== undefined && (
          <Field.Root invalid>
            <Field.Error match>{listError.message}</Field.Error>
          </Field.Root>
        )}
      </div>
    </FormBlock>
  );
}

/** What the agent is told before its first turn; required without goals. */
function KickoffBlock({ subject }: { subject: EditorSubject }) {
  const { control } = useFormContext<AgentFormValues>();
  const catalog = useAgentVariableCatalog();
  const titleId = React.useId();
  const goals = useWatch({ control, name: "goals" });
  const passthroughGoals = useWatch({ control, name: "passthroughGoals" });
  const hasGoals = goals.length + passthroughGoals.length > 0;

  return (
    <FormBlock
      titleId={titleId}
      title={
        <>
          Kickoff message{" "}
          <span className={styles.qualifier}>
            {hasGoals ? "(optional)" : "(required without goals)"}
          </span>
        </>
      }
      className={styles.divided}
      data-testid="agent-kickoff"
    >
      <Controller
        control={control}
        name="kickoffMessage"
        render={({ field, fieldState }) => (
          <Field.Root
            invalid={fieldState.invalid}
            data-testid="agent-kickoff-field"
          >
            <TemplateField
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
              catalog={catalog}
              labelledBy={titleId}
              rows={3}
              error={fieldState.invalid}
              placeholder={
                subject === "none"
                  ? "e.g. Compile this week's failed missions across the fleet and post a summary."
                  : `e.g. List every e-stop in this ${subject} and what preceded it.`
              }
            />
            <Field.Error match>{fieldState.error?.message}</Field.Error>
          </Field.Root>
        )}
      />
    </FormBlock>
  );
}

interface SwitchSubjectDialogProps {
  next: EditorSubject;
  goals: AgentFormValues["goals"];
  /** Goals set through the SDK or API, which only a switch to none removes. */
  keptGoalCount: number;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Asks before switching what an agent with goals runs on, and says what
 * happens to them. To the other kind of record, each becomes the same kind of
 * goal there, keeping what was filled in; a dataset events goal's collection
 * setting has no session counterpart, so switching to a session warns that it
 * is dropped. To nothing specific, every goal is removed.
 */
function SwitchSubjectDialog({
  next,
  goals,
  keptGoalCount,
  onCancel,
  onConfirm,
}: SwitchSubjectDialogProps) {
  const onOpenChange = (open: boolean) => {
    if (!open) {
      onCancel();
    }
  };
  if (next === "none") {
    const count = goals.length + keptGoalCount;
    return (
      <ConfirmDialog
        open
        onOpenChange={onOpenChange}
        title="Run this agent on nothing specific?"
        description={`Goals work on one dataset or session, so an agent that runs on nothing specific has none. Its ${count} ${pluralize("goal", count)} ${count === 1 ? "is" : "are"} removed, and it is no longer launched from dataset or session pages.`}
        confirmLabel={`Remove ${pluralize("goal", count)} and switch`}
        onConfirm={onConfirm}
      />
    );
  }
  const dropsCollection =
    next === "session" && anyGoalUsesImplicitCollection(goals);
  const renamed = goals.map(
    (goal) =>
      `${goalTypeLabel(goal.goal_type)} becomes ${goalTypeLabel(goalForSubject(goal, next).goal_type)}`,
  );
  return (
    <ConfirmDialog
      open
      onOpenChange={onOpenChange}
      title={`Run this agent on a ${next}?`}
      description={`Each goal becomes the same kind of goal on a ${next} and keeps what you filled in: ${[...new Set(renamed)].join("; ")}.`}
      confirmLabel={`Run on a ${next}`}
      onConfirm={onConfirm}
    >
      {dropsCollection && (
        <Banner
          variant="warning"
          title="The collection setting is dropped"
          description="Session events are not added to a collection, so the events goal no longer asks for one at launch."
        />
      )}
    </ConfirmDialog>
  );
}
